import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  resource,
} from '@angular/core';
import {
  User,
  UserProfile,
  UserProfileWagersEnum,
  WAGER_NAMES,
  REWARD_NAMES,
} from '@core/interfaces/user.interface';
import { SessionService } from '@core/services/session.service';
import { SocketService } from '@core/services/socket.service';
import { SvgIconComponent } from 'angular-svg-icon';
import { UserCommonInfoComponent } from '../user-common-info/user-common-info.component';
import { CardComponent } from '../../../core/components/card/card.component';
import { CountUpModule } from 'ngx-countup';
import { sleep } from '@core/utils/sleep.util';

interface UserProfileStats {
  icon: string;
  label: string;
  value: string | number;
  color: string;
  subValue?: string;
}

@Component({
  selector: 'app-user-profile',
  imports: [
    SvgIconComponent,
    UserCommonInfoComponent,
    CardComponent,
    CountUpModule,
  ],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class UserProfileComponent {
  user = input.required<User>();

  countUpOptions = {
    decimalPlaces: 2,
    duration: 1,
  };

  gameIcons: Record<keyof typeof UserProfileWagersEnum, string> = {
    Plinko: 'assets/icons/games/plinko.svg',
    Jackpot: 'assets/icons/games/jackpot.svg',
    King: 'assets/icons/games/king.svg',
    Mines: 'assets/icons/games/mines.svg',
    Towers: 'assets/icons/games/towers.svg',
    Upgrader: 'assets/icons/games/upgrade.svg',
    Wheel: 'assets/icons/games/wheel.svg',
    CaseOpening: 'assets/icons/games/case-openings.svg',
    CaseBattles: 'assets/icons/games/case-battles.svg',
    Coinflip: 'assets/icons/games/coinflip.svg',
  };


  wagers = computed(() => {
    const data = this.profileResource.value()?.profile.wagers ?? {} as UserProfile['wagers'];
    const wagers = (Object.keys(WAGER_NAMES) as (keyof typeof WAGER_NAMES)[]).map(key => ({
      title: WAGER_NAMES[key],
      value: data[key] ?? 0,
    }));

    const total = wagers.reduce((acc, curr) => acc + curr.value, 0);

    return [
      { title: 'Total Wagered', value: total },
      ...wagers,
    ];
  });

  rewards = computed(() => {
    const data = this.profileResource.value()?.profile.rewards ?? {} as UserProfile['rewards'];
    return (Object.keys(REWARD_NAMES) as (keyof typeof REWARD_NAMES)[]).map(key => ({
      name: REWARD_NAMES[key],
      value: data[key] ?? 0,
    }));
  });

  stats = computed(() => {
    const resource = this.profileResource.value();
    const stats: UserProfileStats[] = [];
    const { favorite, favorite_case, luckiest } = resource?.profile ?? {};

    if (favorite) {
      stats.push({
        icon: this.gameIcons[favorite],
        value: this.splitCamelCase(favorite),
        label: 'Favorite Game',
        color: 'yellow',
      });
    }

    if (favorite_case?.name && favorite_case?.image) {
      stats.push({
        icon: favorite_case.image,
        value: favorite_case.name,
        label: 'Favorite Case',
        color: 'green',
        subValue: favorite_case.opens ? `(${favorite_case.opens} opened)` : undefined,
      });
    }

    if (luckiest) {
      stats.push(
        {
          icon: 'assets/icons/ui/multiplier.svg',
          label: `Luckiest Win`,
          value: `${luckiest.multiplier}x`,
          color: 'purple',
        },
        {
          icon: 'assets/icons/core/price.svg',
          label: 'Biggest win',
          value: luckiest.won,
          color: 'blue',
        }
      );
    }

    return stats;
  });

  profileResource = resource({
    params: () => {
      return {
        steamid: this.user().steamid,
      };
    },
    loader: async (params) => {
      const request = params.params;

      const data: { 0: boolean; 1: UserProfile } =
        await this.socketService.emitArgs('load_profile', {
          steamid: request.steamid,
        });

      const isPrivate = data[0];
      const profile = data[1];
      await sleep(500);
      return { isPrivate, profile };
    },
  });

  constructor(
    public sessionService: SessionService,
    private socketService: SocketService
  ) {}

  private splitCamelCase(str: string) {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  }
}
